// ============================================================
// BuildingSmartDevices.jsx
//
// Course content page for "Building Smart Devices" lesson.
// Rewritten to fix a code example with two compile-breaking bugs
// and one misleading comment:
//   1) it called `triggerAlert(...)`, a function that was never
//      defined anywhere — undefined reference,
//   2) it had no `setup()` at all — the ESP32 Arduino core expects
//      both setup() and loop() to exist and will fail to link
//      without one,
//   3) the comment claimed "Deep Sleep for 1 hour" but the code
//      never configured a wakeup timer — esp_deep_sleep_start()
//      with no wakeup source configured sleeps indefinitely, not
//      for an hour. Also added the detail that waking from deep
//      sleep restarts execution at setup(), not loop() — a common
//      point of confusion.
// Also removed two dead imports (FaMicrochip, MdOutlineDynamicForm),
// fixed the copy button (previously fired-and-forgot the clipboard
// write), matched the house dark-purple/goldenrod look, and moved
// to arrow-function components with Material Icons only.
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Paper,
    Stack,
    IconButton,
    Avatar,
} from "@mui/material";

// ── Icons — @mui/icons-material only ──
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import MemoryIcon from "@mui/icons-material/Memory";
import WifiIcon from "@mui/icons-material/Wifi";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import BuildIcon from "@mui/icons-material/Build";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import TerminalIcon from "@mui/icons-material/Terminal";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import ElectricalServicesIcon from "@mui/icons-material/ElectricalServices";

// ── Brand tokens (matches IoTDashboard.jsx / SensorsActuators.jsx) ──
const BRAND = {
    bg: "#100720",
    surface: "rgba(16,7,32,0.85)",
    border: "rgba(255,255,255,0.1)",
    gold: "goldenrod",
    teal: "#2DD4BF",
    white: "#FAFAF9",
    muted: "rgba(255,255,255,0.65)",
    codeBg: "#0b0515",
};

const ANATOMY = [
    { icon: <MemoryIcon />, label: "Microcontroller", description: "The decision maker (ESP32, STM32)." },
    { icon: <WifiIcon />, label: "Connectivity", description: "Communication via Wi-Fi, LoRa, or BLE." },
    { icon: <BatteryFullIcon />, label: "Power Supply", description: "Battery, solar, or mains power systems." },
    { icon: <BuildIcon />, label: "Sensors/Actuators", description: "Perception and physical response." },
    { icon: <Inventory2Icon />, label: "Enclosure", description: "Industrial design and protection (e.g. IP65)." },
    { icon: <IntegrationInstructionsIcon />, label: "Logic", description: "The firmware running the control loop." },
];

const LIFECYCLE = [
    { step: "01", title: "Problem Definition", desc: "Identify user needs and environmental constraints." },
    { step: "02", title: "Hardware Selection", desc: "Balance power consumption vs. processing capability." },
    { step: "03", title: "PCB Design", desc: "Minimize footprint and manage thermal dissipation." },
    { step: "04", title: "Firmware Development", desc: "Write efficient code with sleep modes for longevity." },
];

const APPLICATIONS = ["Smart Commercial Buildings", "Agricultural Automation", "Industrial Asset Tracking", "Energy Management Systems"];

// A complete, compiling sketch — the original called an undefined
// triggerAlert() function, had no setup(), and claimed "1 hour" of
// sleep without ever configuring a wakeup timer.
const SMART_MONITOR_SKETCH = `#include <esp_sleep.h>

#define MOISTURE_PIN A0
#define MOISTURE_THRESHOLD 300
#define SLEEP_SECONDS (60 * 60) // 1 hour

void setup() {
  Serial.begin(115200);

  int moisture = analogRead(MOISTURE_PIN);
  if (moisture < MOISTURE_THRESHOLD) {
    Serial.println("ALERT: Low water level");
    // Wire this to an MQTT publish — see the Cloud Integration
    // & APIs and IoT Protocols lessons for a working example.
  }

  // Edge efficiency: sleep for exactly one hour, then wake.
  esp_sleep_enable_timer_wakeup(SLEEP_SECONDS * 1000000ULL); // microseconds
  esp_deep_sleep_start();
}

void loop() {
  // Never reached: waking from deep sleep restarts execution
  // at setup(), not loop().
}`;

// Sub-components (arrow-function style, defined before first use)

const ComponentTag = ({ icon, label, description }) => (
    <Paper
        elevation={0}
        sx={{
            p: 3,
            background: BRAND.surface,
            border: `1px solid ${BRAND.border}`,
            borderRadius: 4,
            transition: "0.3s",
            height: "100%",
            "&:hover": { borderColor: BRAND.gold, transform: "translateY(-4px)" },
        }}
    >
        <Stack direction="row" spacing={2} alignItems="flex-start">
            <Avatar sx={{ bgcolor: "rgba(218,165,32,0.15)", color: BRAND.gold }}>{icon}</Avatar>
            <Box>
                <Typography variant="subtitle1" sx={{ color: BRAND.white, fontWeight: 700 }}>{label}</Typography>
                <Typography variant="caption" sx={{ color: BRAND.muted }}>{description}</Typography>
            </Box>
        </Stack>
    </Paper>
);

const CodeTerminal = ({ code, label }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard API isn't available in every browser/context.
        }
    };

    return (
        <Box sx={{ background: BRAND.codeBg, borderRadius: 3, border: `1px solid ${BRAND.border}`, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", background: "rgba(255,255,255,0.03)" }}>
                <Typography variant="caption" sx={{ color: BRAND.gold, fontWeight: 700, fontFamily: "monospace" }}>{label}</Typography>
                <IconButton size="small" onClick={handleCopy} sx={{ color: BRAND.muted }}>
                    {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Box sx={{ p: 2.5, overflowX: "auto" }}>
                <Typography component="pre" sx={{ fontFamily: "'Fira Code', monospace", fontSize: 13, color: "#FCD34D", lineHeight: 1.5 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
};

// ── Main Page ───────────────────────────────────────────────────

const BuildingSmartDevices = () => (
    <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
        <Container maxWidth="lg">
            {/* HERO SECTION */}
            <Box sx={{ mb: 10, textAlign: { xs: "center", md: "left" } }}>
                <Chip
                    icon={<SchoolIcon style={{ color: BRAND.gold }} />}
                    label="Applied Engineering · Module 10"
                    sx={{ bgcolor: "rgba(218,165,32,0.1)", color: BRAND.gold, border: `1px solid ${BRAND.border}`, mb: 2 }}
                />
                <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                    Building <span style={{ color: BRAND.teal }}>Smart Devices</span>
                </Typography>
                <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                    Transform a prototype into a product. Smart devices combine physical enclosures, power
                    management, and embedded logic to solve real-world problems.
                </Typography>
            </Box>

            {/* CORE ANATOMY */}
            <Box sx={{ mb: 10 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                    <ElectricalServicesIcon sx={{ fontSize: 32, color: BRAND.gold }} />
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>Anatomy of a Smart Device</Typography>
                </Box>
                <Grid container spacing={3}>
                    {ANATOMY.map((item) => (
                        <Grid item xs={12} md={4} key={item.label}>
                            <ComponentTag {...item} />
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* DESIGN WORKFLOW */}
            <Box sx={{ mb: 10 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                    <PrecisionManufacturingIcon sx={{ fontSize: 32, color: BRAND.gold }} />
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>The Design Lifecycle</Typography>
                </Box>
                <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                        <Stack spacing={3}>
                            {LIFECYCLE.map((item) => (
                                <Box key={item.step} sx={{ display: "flex", gap: 2 }}>
                                    <Typography variant="h4" sx={{ color: "rgba(255,255,255,0.08)", fontWeight: 900 }}>{item.step}</Typography>
                                    <Box>
                                        <Typography variant="subtitle1" sx={{ color: BRAND.white, fontWeight: 700 }}>{item.title}</Typography>
                                        <Typography variant="body2" sx={{ color: BRAND.muted }}>{item.desc}</Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <CodeTerminal label="SMART_MONITOR.ino" code={SMART_MONITOR_SKETCH} />
                    </Grid>
                </Grid>
            </Box>

            {/* VISUAL CASE STUDIES */}
            <Box sx={{ mb: 10 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 4, textAlign: "center" }}>Industrial Applications</Typography>
                <Grid container spacing={2}>
                    {APPLICATIONS.map((text) => (
                        <Grid item xs={12} sm={6} md={3} key={text}>
                            <Paper sx={{ p: 2, textAlign: "center", bgcolor: BRAND.surface, border: `1px solid ${BRAND.border}` }}>
                                <Typography variant="caption" sx={{ color: BRAND.gold, fontWeight: 800 }}>{text}</Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* FOOTER CTA */}
            <Box sx={{ p: 8, borderRadius: 8, textAlign: "center", background: "rgba(255,255,255,0.03)", border: `1px solid ${BRAND.border}` }}>
                <TerminalIcon sx={{ fontSize: 40, color: BRAND.gold, mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Build Your First Device?</Typography>
                <Typography sx={{ color: BRAND.muted, mb: 4 }}>Turn your breadboard prototype into a production-ready smart product.</Typography>
                <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
                    <Chip label="Start Capstone Project" onClick={() => {}} sx={{ bgcolor: BRAND.gold, color: BRAND.bg, fontWeight: 800, px: 3 }} />
                    <Chip label="Bill of Materials (BOM) Template" variant="outlined" onClick={() => {}} sx={{ borderColor: BRAND.muted, color: BRAND.white }} />
                </Stack>
            </Box>
        </Container>
    </Box>
);

export default BuildingSmartDevices;
